import { expected, schCommentaryV2 } from '@aloft/toolkit-shared';
import { Flex, Grid, Tabs } from '@aloft/toolkit-sui';

import { motion } from 'framer-motion';
import { cloneDeep } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';

import { useZodForm } from '../../hooks/useZodForm.ts';
import { setCommentaryV2 } from '../../redux/features/commentarySlice.ts';
import { selectCommentaryV2 } from '../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { useRequiredFieldFocusEffect } from '../alerts/useRequiredFieldFocusEffect.ts';
import { tabPanelsMap, tabs } from './consts.tsx';
import { Tab, TabRef, TabRefs } from './types.ts';

export const AppraisifyCommentaryModule = () => {
  useRequiredFieldFocusEffect();
  const dispatch = useAppDispatch();
  const commentaryV2 = useAppSelector(selectCommentaryV2);

  const [activeTab, setActiveTab] = useState<Tab>(expected(tabs[0]).value);
  const sectionRefs = useRef<TabRefs>(
    Object.fromEntries(tabs.map((tab) => [tab.value, null])) as TabRefs,
  );

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const tabsContainerRef = useRef<HTMLDivElement | null>(null);

  const handleScroll = () => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const { clientHeight, scrollHeight, scrollTop } = scrollContainer;

    const isAtBottom = scrollHeight - scrollTop - clientHeight <= 1;

    if (isAtBottom) {
      setActiveTab(expected(tabs[tabs.length - 1]).value);
      return;
    }

    const entries = Object.entries(sectionRefs.current) as [Tab, TabRef][];

    const newActiveTab =
      entries
        .filter(([, ref]) => ref !== null)
        .map(([tab, ref]) => ({ tab, rect: (ref as HTMLElement).getBoundingClientRect() }))
        .filter(({ rect }) => rect.top + rect.height / 4 >= containerRect.top)
        .map(({ tab, rect }) => ({
          tab,
          distance: Math.abs(rect.top - containerRect.top),
        }))
        .sort((a, b) => a.distance - b.distance)[0] ?? null;

    if (newActiveTab) {
      setActiveTab(newActiveTab.tab);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    container?.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const updateActiveTabFromHash = () => {
      const hash = window.location.hash.slice(1);
      if (hash && tabs.some((t) => t.value === hash)) {
        setActiveTab(hash as Tab);
        requestAnimationFrame(() => {
          sectionRefs.current[hash as Tab]?.scrollIntoView({ behavior: 'instant', block: 'start' });
        });
      }
    };

    updateActiveTabFromHash();

    window.addEventListener('hashchange', updateActiveTabFromHash);

    return () => {
      window.removeEventListener('hashchange', updateActiveTabFromHash);
    };
  }, []);

  useEffect(() => {
    if (activeTab) window.history.replaceState(null, '', `#${activeTab}`);

    const container = tabsContainerRef.current;
    if (!container) return;

    requestAnimationFrame(() => {
      const activeTabEl = container.querySelector('.selected');
      if (activeTabEl !== null) {
        activeTabEl.scrollIntoView({ behavior: 'instant', inline: 'center' });
      }
    });
  }, [activeTab]);

  const handleTabOnClick = (tab: Tab) => {
    if (activeTab !== tab) setActiveTab(tab);
    sectionRefs.current[tab]?.scrollIntoView({ behavior: 'instant', block: 'start' });
  };

  const zodForm = useZodForm({
    schema: schCommentaryV2,
    defaultValues: cloneDeep(commentaryV2),
  });

  const { watch } = zodForm;

  useEffect(() => {
    const subscription = watch((formValues) => {
      const parsedFormValues = schCommentaryV2.safeParse(formValues);
      if (!parsedFormValues.success) {
        console.error('Invalid form values:', parsedFormValues.error); // TODO: Add visual error feedback?
        return;
      }

      dispatch(setCommentaryV2(parsedFormValues.data));
    });

    return () => subscription.unsubscribe();
  }, [dispatch, watch]);

  return (
    <FormProvider {...zodForm}>
      <Grid width="100%" height="100%" overflow="hidden" gridTemplateColumns="1fr 2fr">
        <Flex
          flexDirection="column"
          width="100%"
          overflow="hidden"
          borderStyle="solid"
          borderRightWidth="1px"
          borderRightColor="strokeDefault"
        >
          <Flex
            ref={tabsContainerRef}
            minHeight="44px"
            overflowX="auto"
            __dangerDoNotUseSx={{ whiteSpace: 'nowrap' }}
          >
            <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={handleTabOnClick} />
          </Flex>
          <Flex ref={scrollContainerRef} flexDirection="column" overflow="auto" minHeight={0}>
            {tabs.map(({ value }) => {
              const { Left } = expected(tabPanelsMap[value]);
              return (
                <Flex
                  key={value}
                  ref={(el) => {
                    sectionRefs.current[value] = el;
                  }}
                  flexDirection="column"
                  flexShrink={0}
                  paddingBottom="50px"
                  backgroundColor={value === activeTab ? 'surface3' : 'surface1'}
                >
                  <Left />
                </Flex>
              );
            })}
          </Flex>
        </Flex>
        <Flex
          flexDirection="column"
          overflow="hidden"
          __dangerDoNotUseSx={{ position: 'relative' }}
        >
          {tabs.map(({ value }) => {
            const isActive = value === activeTab;
            const { Right } = expected(tabPanelsMap[value]);
            return (
              <motion.div
                key={value}
                initial={false}
                animate={{ opacity: isActive ? 1 : 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  overflow: 'hidden',
                  pointerEvents: isActive ? 'auto' : 'none',
                }}
              >
                <Right />
              </motion.div>
            );
          })}
        </Flex>
      </Grid>
    </FormProvider>
  );
};
